import { getWorkOrderDisplayNumber, CONTEXT } from '../../utils/workOrderDisplayNumber';
import { getNextAction } from '../maintenance/MaintenanceWorkflowStatusCard';
import { ROLES } from '../../constants/roles';

export const LAB_LANES = {
  planning: {
    key: 'planning',
    path: '/mro/lab/planning',
    title: 'Planning',
    subtitle: 'Coordinate dates, store flags, external and CAMO handoffs before MRO accept.',
    accent: '#1565c0',
    accentSoft: 'rgba(21, 101, 192, 0.08)'
  },
  manager: {
    key: 'manager',
    path: '/mro/lab/manager',
    title: 'MRO Manager',
    subtitle: 'Accept work packages, assign engineers, and close completed jobs.',
    accent: '#e65100',
    accentSoft: 'rgba(230, 81, 0, 0.08)'
  },
  engineer: {
    key: 'engineer',
    path: '/mro/lab/engineer',
    title: 'MRO Engineer',
    subtitle: 'Execute assigned tasks, AMP checks, and engineer completion.',
    accent: '#2e7d32',
    accentSoft: 'rgba(46, 125, 50, 0.08)'
  },
  qm: {
    key: 'qm',
    path: '/mro/lab/qm',
    title: 'MRO Quality',
    subtitle: 'Verify engineer completion and return work for rework when needed.',
    accent: '#6a1b9a',
    accentSoft: 'rgba(106, 27, 154, 0.08)'
  }
};

export const LAB_LANE_LIST = Object.values(LAB_LANES);

const isExternalJob = (wo) =>
  wo?.jobSource === 'EXTERNAL_API'
  || wo?.jobSource === 'EXTERNAL_MANUAL'
  || wo?.jobSource === 'EXTERNAL_PORTAL';

export const isPendingManagerLane = (wo) => {
  if (!wo || wo.status === 'CANCELLED') return false;
  if (
    wo.needsMROAction
    && wo.planningApproved
    && !wo.mroAccepted
    && !wo.mroRejectedAt
  ) {
    return true;
  }
  if (
    wo.mroEngineerCompleted
    && wo.mroQmCompletionAcknowledged
    && wo.status === 'IN_PROGRESS'
  ) {
    if (isExternalJob(wo)) return true;
    return Boolean(wo.camoQmCompletionAcknowledged);
  }
  return false;
};

export const isPendingEngineerLane = (wo) =>
  Boolean(
    wo
    && wo.status !== 'CANCELLED'
    && wo.mroAccepted
    && wo.status === 'IN_PROGRESS'
    && !wo.mroEngineerCompleted
  );

export const isPendingQmLane = (wo) =>
  Boolean(
    wo
    && wo.status !== 'CANCELLED'
    && wo.mroEngineerCompleted
    && wo.status === 'IN_PROGRESS'
    && !wo.mroQmCompletionAcknowledged
  );

export const managerLaneLabel = (wo) => {
  if (
    wo.needsMROAction
    && wo.planningApproved
    && !wo.mroAccepted
    && !wo.mroRejectedAt
  ) {
    return 'MRO acceptance';
  }
  if (
    wo.mroEngineerCompleted
    && wo.mroQmCompletionAcknowledged
    && wo.status === 'IN_PROGRESS'
  ) {
    return 'Mark work order completed';
  }
  return 'Manager action';
};

export const mroReferenceNumber = (wo) =>
  (wo?.mroJobNumber && String(wo.mroJobNumber).trim())
  || getWorkOrderDisplayNumber(wo, CONTEXT.MRO)
  || '-';

export const camoWorkOrderNumber = (wo) =>
  (wo?.workOrderNumber && String(wo.workOrderNumber).trim())
  || '-';

const normalize = (value) => `${value || ''}`.trim().toLowerCase();

export const matchesSearch = (wo, query) => {
  const q = normalize(query);
  if (!q) return true;
  const hay = [
    mroReferenceNumber(wo),
    camoWorkOrderNumber(wo),
    wo?.title,
    wo?.aircraftId?.registration,
    wo?.status
  ].filter(Boolean).join(' ').toLowerCase();
  return hay.includes(q);
};

export const pendingActionLabelForLane = (laneKey, wo) => {
  if (laneKey === 'planning') {
    return wo.planningTask?.label
      || getNextAction(wo, ROLES.PLANNING_MANAGER)?.action
      || 'Planning';
  }
  if (laneKey === 'manager') return managerLaneLabel(wo);
  if (laneKey === 'engineer') return 'MRO engineer completion';
  return 'MRO QM acknowledgment';
};

export const formatShortDate = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toISOString().slice(0, 10);
};
